import {
  ICellRendererParams,
  ISetFilterParams,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import {
  HeliosColumnTypes,
  heliosDefaultColWidth,
  NumberFilterOptionsWithAbsolute,
  NumberFilterOptionsWithoutEquals,
  TextFilterOptionsWithContainsIn,
} from '@helios/ag-grid-config'
import { formatDates, formatDateTime } from '@helios/common-api'
import { NfColumnTypes, NfTooltipParams } from '@northfork/ag-grid'
import { Observable } from 'rxjs'
import {
  Account,
  AccountsSetValues,
  accountTypes,
  accountTypesFilterCellRenderer,
} from '../models'
import { getBooleanTypeCellRenderer } from './booleanTypeCellRenderer'
import {
  reconRunStatusTypesCellRenderer,
  reconRunStatusTypesCellRendererWithText,
} from './reconStatusCellRenderers'

export const aggregateAccountColDefs = (
  accountPath: (field: keyof Account) => string,
  setValues$: Observable<AccountsSetValues>,
) => {
  return [
    {
      field: accountPath('accountId'),
      headerName: 'Account ID',
      width: heliosDefaultColWidth.Id,
      type: NfColumnTypes.Identifier,
      valueFormatter: undefined,
      sortable: true,
      filterParams: {
        filterOptions: NumberFilterOptionsWithoutEquals,
      },
    },
    {
      field: accountPath('accountName'),
      headerName: 'Account Name',
      sortable: true,
      filterParams: {
        filterOptions: TextFilterOptionsWithContainsIn,
      },
    },
    {
      field: accountPath('clientId'),
      headerName: 'Client ID',
      type: NfColumnTypes.Identifier,
      valueFormatter: undefined,
      initialHide: true,
      sortable: true,
      filterParams: {
        filterOptions: NumberFilterOptionsWithAbsolute,
      },
    },
    {
      ...getAccountsSetValuesFilterParams('clients', setValues$),
      field: accountPath('clientName'),
      headerName: 'Client Name',
      sortable: true,
    },
    {
      field: accountPath('lastPerseusRun'),
      headerName: 'Last Recon Run',
      type: HeliosColumnTypes.ShortDateTime,
      sortable: true,
      tooltipValueGetter: (params: ITooltipParams) => {
        return formatDateTime(params.value)
      },
    },
    {
      field: accountPath('lastPerseusRunReconDate'),
      headerName: 'Last Recon Run Recon Date',
      type: NfColumnTypes.Date,
      width: heliosDefaultColWidth.DateCol,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
      },
      tooltipValueGetter: (params: ITooltipParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
      },
    },
    {
      field: accountPath('lastPerseusRunOmegaAchieved'),
      headerName: 'Last Recon Run Omega Achieved',
      type: HeliosColumnTypes.Boolean,
      sortable: true,
    },
    {
      field: accountPath('omegaDate'),
      headerName: 'Omega Date',
      type: NfColumnTypes.Date,
      width: heliosDefaultColWidth.DateCol,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
      },
      tooltipValueGetter: (params: ITooltipParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
      },
    },
    {
      field: accountPath('startDate'),
      headerName: 'Account Start Date',
      type: NfColumnTypes.Date,
      width: heliosDefaultColWidth.DateCol,
      initialHide: true,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
      },
      tooltipValueGetter: (params: ITooltipParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
      },
    },
    {
      field: accountPath('parentClientId'),
      headerName: 'Ult Parent Client ID',
      type: NfColumnTypes.Identifier,
      sortable: true,
      filterParams: {
        filterOptions: NumberFilterOptionsWithAbsolute,
      },
    },
    {
      ...getAccountsSetValuesFilterParams('parentClients', setValues$),
      field: accountPath('parentClientName'),
      headerName: 'Ult Parent',
      sortable: true,
    },
    {
      type: HeliosColumnTypes.ShortDateTime,
      field: accountPath('reconTimestamp'),
      headerName: 'Recon Timestamp',
      sortable: true,
    },
    {
      ...getBooleanTypeCellRenderer('mdi-check-outline'),
      field: accountPath('hasOmegaDateToday'),
      headerName: 'Omega Date Today',
      initialHide: true,
      sortable: true,
    },
    {
      field: accountPath('isReconciling'),
      headerName: 'Reconciling',
      sortable: true,
      initialHide: true,
      type: HeliosColumnTypes.Boolean,
    },
    {
      ...getAccountsSetValuesPerseusRunStatus(setValues$),
      field: accountPath('perseusRunStatus'),
      headerName: 'Recon Run Status',
      cellRenderer: reconRunStatusTypesCellRenderer,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
      tooltipComponentParams: {
        showTooltip: () => true,
      } as NfTooltipParams,
      width: heliosDefaultColWidth.LargeIcon,
      sortable: true,
    },
  ]
}

export const portfolioAndSimplesColDefs = (
  accountPath: (field: keyof Account) => string,
  setValues$: Observable<AccountsSetValues>,
) => {
  return [
    {
      ...getAccountsSetValuesFilterParams('accountTypes', setValues$),
      field: accountPath('accountType'),
      headerName: 'Account Type',
      width: heliosDefaultColWidth.SmallIcon,
      cellRenderer: (params: ICellRendererParams) =>
        accountTypes.get(params.value)?.icon ?? '',
      tooltipValueGetter: (params: ITooltipParams) =>
        accountTypes.get(params.value)?.toolTip ?? '',
      tooltipComponentParams: {
        showTooltip: () => true,
      } as NfTooltipParams,
      sortable: true,
      filterParams: {
        cellRenderer: accountTypesFilterCellRenderer,
        values: params => {
          setValues$.subscribe(values => params.success(values.accountTypes))
        },
      } as ISetFilterParams,
    },
    {
      field: accountPath('custodyAccountNumber'),
      headerName: 'Custody Account ID',
      sortable: true,
      filterParams: {
        filterOptions: TextFilterOptionsWithContainsIn,
      },
    },
    {
      field: accountPath('custodyDataFeeds'),
      headerName: 'Custody Data Feeds',
      initialHide: true,
      sortable: true,
      filterParams: {
        filterOptions: TextFilterOptionsWithContainsIn,
      },
    },
    {
      field: accountPath('tradingDataFeeds'),
      headerName: 'Trading Data Feeds',
      initialHide: true,
      sortable: true,
      filterParams: {
        filterOptions: TextFilterOptionsWithContainsIn,
      },
    },
    {
      field: accountPath('custodyReconciled'),
      headerName: 'Custody Reconciled',
      type: NfColumnTypes.Date,
      width: heliosDefaultColWidth.DateCol,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
      },
    },
    {
      field: accountPath('tradingReconciled'),
      headerName: 'Trading Reconciled',
      type: NfColumnTypes.Date,
      width: heliosDefaultColWidth.DateCol,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
      },
    },
    {
      field: accountPath('clientVisibleFoundTotal'),
      headerName: 'Active Breaks',
      initialHide: false,
      sortable: true,
      type: NfColumnTypes.Integer,
      width: heliosDefaultColWidth.MediumIcon,
      filter: false,
    },
    {
      field: accountPath('clientVisibleHeldTotal'),
      headerName: 'Held Breaks',
      initialHide: false,
      sortable: true,
      type: NfColumnTypes.Integer,
      width: heliosDefaultColWidth.MediumIcon,
      filter: false,
    },
    {
      ...getAccountsSetValuesFilterParams('sleeveAggregateIds', setValues$),
      field: accountPath('sleeveAggregateId'),
      headerName: 'Parent Portfolio Account ID',
      sortable: true,
      type: NfColumnTypes.Identifier,
      width: heliosDefaultColWidth.Id,
    },
    ...aggregateAccountColDefs(accountPath, setValues$),
  ]
}

const getAccountsSetValuesFilterParams = (
  key: keyof AccountsSetValues,
  setValues$: Observable<AccountsSetValues>,
) => {
  return {
    filter: 'agSetColumnFilter',
    filterParams: {
      refreshValuesOnOpen: true,
      defaultToNothingSelected: true,
      values: params => {
        setValues$.subscribe(values => params.success(values[key] ?? []))
      },
      buttons: ['apply', 'reset'],
    } as ISetFilterParams,
  }
}

const getAccountsSetValuesPerseusRunStatus = (
  setValues$: Observable<AccountsSetValues>,
) => {
  return {
    filter: 'agSetColumnFilter',
    filterParams: {
      refreshValuesOnOpen: true,
      defaultToNothingSelected: true,
      cellRenderer: reconRunStatusTypesCellRendererWithText,
      values: params => {
        setValues$.subscribe(values => {
          return params.success(values.perseusRunStatus)
        })
      },
      buttons: ['apply', 'reset'],
    } as ISetFilterParams,
  }
}
